import { IResumeItem } from '@medely/types';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import omit from 'lodash/omit';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useToast } from '@medely/ui-kit';

type workHistoryProps = {
  values: Partial<IResumeItem & { position_id?: number | null }>;
  assignment_request_id?: number | null;
};

export const useWorkHistoryActions = (isClinical: boolean) => {
  const { axios } = useNetworkRequest();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: editWorkHistory, isLoading: isEditingWorkHistory } = useMutation(
    ({ values, assignment_request_id }: workHistoryProps) =>
      axios.put(`/v3/professional/work_histories/${values?.id}`, {
        assignment_request_id,
        work_history: {
          ...omit(values, ['id', 'isWorkGap', 'missing_information_list', 'position', 'state']),
          position_id: values?.position_id,
          clinical: isClinical,
        },
      }),
    {
      onSuccess: () => {
        toast.success('Work history updated!');
        queryClient.invalidateQueries(['GetWorkHistoriesForResumeBuilder']);
      },
      onError: () => toast.error('We had an issue, please try again'),
    },
  );

  const { mutateAsync: addWorkHistory, isLoading: isAddingWorkHistory } = useMutation(
    ({ values, assignment_request_id }: workHistoryProps) =>
      axios.post('/v3/professional/work_histories', {
        assignment_request_id,
        work_history: {
          ...omit(values, ['position']),
          position_id: values?.position_id,
          clinical: isClinical,
        },
      }),
    {
      onSuccess: () => {
        toast.success('Work history added!');
        queryClient.invalidateQueries(['GetWorkHistoriesForResumeBuilder']);
      },
      onError: () => toast.error('We had an issue, please try again'),
    },
  );

  return {
    editWorkHistory,
    addWorkHistory,
    isEditingWorkHistory,
    isAddingWorkHistory,
  };
};
